import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldOff } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import Badge from '@/components/ui/Badge';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData, DirtyCell } from '@/components/admin/DataTable';
import { useUIStore } from '@/stores/ui-store';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string | null;
  role: string;
  order_count: number;
  total_spent: number;
  last_order_at: string | null;
  created_at: string;
}

interface CustomersResponse {
  customers: Customer[];
  pagination: PaginationData;
}

const getInitials = (firstName: string, lastName: string): string => {
  return `${(firstName || '?').charAt(0)}${(lastName || '?').charAt(0)}`.toUpperCase();
};

const columns: ColumnDef<Customer>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (row) => `${row.first_name} ${row.last_name}`,
    sortable: true,
    hideable: false,
    cell: ({ row }) => (
      <Link to={`/admin/customers/${row.id}`} className="flex items-center gap-3 group">
        {row.avatar_url ? (
          <img src={row.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-blue-600">
              {getInitials(row.first_name, row.last_name)}
            </span>
          </div>
        )}
        <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
          {row.first_name} {row.last_name}
        </span>
      </Link>
    ),
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    sortable: true,
  },
  {
    id: 'role',
    header: 'Role',
    accessorKey: 'role',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'User', value: 'user' },
      { label: 'Admin', value: 'admin' },
    ],
    cell: ({ row }) =>
      row.role === 'admin' ? (
        <Badge variant="blue">
          <Shield className="h-3 w-3 mr-1 inline" aria-hidden="true" />
          Admin
        </Badge>
      ) : (
        <span className="text-sm text-gray-500">User</span>
      ),
  },
  {
    id: 'order_count',
    header: 'Orders',
    accessorKey: 'order_count',
    sortable: true,
    align: 'right',
  },
  {
    id: 'total_spent',
    header: 'Total Spent',
    accessorKey: 'total_spent',
    sortable: true,
    align: 'right',
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">{formatCurrency(row.total_spent)}</span>
    ),
  },
  {
    id: 'last_order_at',
    header: 'Last Order',
    accessorKey: 'last_order_at',
    sortable: true,
    hideable: true,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {row.last_order_at ? formatDate(row.last_order_at) : 'Never'}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: 'Joined',
    accessorKey: 'created_at',
    sortable: true,
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">{formatDate(row.created_at)}</span>
    ),
  },
];

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState, markDirty } = useDataTableState({
    storageKey: 'admin-customers',
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultLimit: 25,
    columns,
  });

  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        if (state.sort) params.set('sort', state.sort);
        params.set('order', state.order);

        const response = await api.get<CustomersResponse>(`/admin/customers?${params.toString()}`);
        setCustomers(response.customers);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customers');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomers();
  }, [state.page, state.limit, state.search, state.sort, state.order]);

  const handleBatchSave = useCallback(async (changes: DirtyCell[]) => {
    const errors: string[] = [];
    for (const cell of changes) {
      try {
        if (cell.columnId === 'role') {
          await api.patch(`/admin/customers/${cell.rowId}/role`, { role: cell.value });
          setCustomers((prev) =>
            prev.map((c) => (c.id === cell.rowId ? { ...c, role: cell.value as string } : c))
          );
        }
      } catch (err: any) {
        errors.push(err.message || 'Failed to update');
      }
    }

    if (errors.length > 0) {
      addToast(`${errors.length} update(s) failed`, 'error');
      throw new Error('Some updates failed');
    }
    addToast(`${changes.length} customer(s) updated`, 'success');
  }, [addToast]);

  const handleDirtyChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      markDirty(rowId, columnId, value, originalValue);
    },
    [markDirty]
  );

  const bulkActions: BulkAction<Customer>[] = [
    {
      id: 'set-admin',
      label: 'Set Admin',
      icon: <Shield className="w-4 h-4 mr-1" />,
      variant: 'primary',
      confirmMessage: 'Grant admin access to the selected customers?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.patch(`/admin/customers/${row.id}/role`, { role: 'admin' });
        }
        setCustomers((prev) =>
          prev.map((c) => (rows.some((r) => r.id === c.id) ? { ...c, role: 'admin' } : c))
        );
        addToast(`${rows.length} customer(s) set to admin`, 'success');
      },
    },
    {
      id: 'set-user',
      label: 'Set User',
      icon: <ShieldOff className="w-4 h-4 mr-1" />,
      variant: 'outline',
      confirmMessage: 'Remove admin access from the selected customers?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.patch(`/admin/customers/${row.id}/role`, { role: 'user' });
        }
        setCustomers((prev) =>
          prev.map((c) => (rows.some((r) => r.id === c.id) ? { ...c, role: 'user' } : c))
        );
        addToast(`${rows.length} customer(s) set to user`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
        <p className="mt-1 text-gray-600">Manage your customer base and view order history</p>
      </div>

      <DataTable
        columns={columns}
        data={customers}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        onBatchSave={handleBatchSave}
        onDirtyChange={handleDirtyChange}
        bulkActions={bulkActions}
        searchPlaceholder="Search by name or email..."
      />
    </div>
  );
}
